<template>
	<BaseLoading
		v-if="isLoadingGetSingle"
	/>
	<div
		v-else
		class="main-wrapper form-custom"
	>
		<section>
			<CRow class="mb-4">
				<CCol lg="12">
					<h4 class="m-0">
						{{ $t('message.online_stock_rule_info') }}
					</h4>
				</CCol>
			</CRow>
			<CRow>
				<CCol lg="12">
					<CInput
						v-model="$v.form.name.$model"
						:label="`${$t('message.allocation_rule_name')}*`"
						:placeholder="$t('message.e_g_bangkok_area')"
						:is-valid="formState.name.valid"
						:invalid-feedback="formState.name.error"
						:disabled="isUser"
					/>
				</CCol>
			</CRow>
			<CRow>
				<CCol lg="6" class="d-flex flex-column">
					<label>
						{{ $t('message.priority') }}*
					</label>
					<span class="typo-helper-text">
						{{ $t('message.zero_is_highest_score_99_is_lowest_score') }}
					</span>
				</CCol>
				<CCol lg="6">
					<BaseInputNumber
						v-model="$v.form.priority.$model"
						:is-valid="formState.priority.valid"
						:invalid-feedback="formState.priority.error"
						:disabled="isUser"
						text-align="left"
						class="w-136px ml-auto"
					/>
				</CCol>
			</CRow>

			<CRow>
				<CCol lg="12" class="mt-4">
					<BaseSwitch
						v-model="form.status"
						:label="$t('message.status')"
						:checked-text="$t('message.active')"
						:unchecked-text="$t('message.inactive')"
					/>
				</CCol>
			</CRow>

			<CRow>
				<CCol>
					<CCol>
						<hr class="mt-4 mb-4">
					</CCol>
				</CCol>
			</CRow>
		</section>

		<section class="mt-3">
			<SelectBranch
				:values="selectedBranchValues"
				:check-all="selectedBranchCheckAll"
				:temporary="tempBranches"
				:is-valid="formState.branch.valid"
				:disabled="isUser"
				@onDone="onSelectBranchDone"
			/>
			<CRow class="mt-4">
				<CCol lg="6">
					<label>
						{{ $t('message.ratio_to_online_stock') }}*
					</label>
				</CCol>
				<CCol lg="6" class="text-right">
					<BaseInputNumber
						v-model="$v.form.ratio.$model"
						:is-valid="formState.ratio.valid"
						:invalid-feedback="formState.ratio.error"
						:disabled="isUser"
						append-text="%"
						text-align="left"
						class="w-136px ml-auto"
					/>
				</CCol>
			</CRow>
			<CRow class="mt-4">
				<CCol lg="6">
					<label>
						{{ $t('message.minimum_qty_for_branches') }}*
					</label>
				</CCol>
				<CCol lg="6" class="text-right">
					<BaseInputNumber
						v-model="$v.form.minQty.$model"
						:append-text="$t('message.qty')"
						:is-valid="formState.minQty.valid"
						:invalid-feedback="formState.minQty.error"
						:disabled="isUser"
						text-align="left"
						class="w-136px ml-auto"
					/>
				</CCol>
			</CRow>
			<CRow class="mt-4">
				<CCol>
					<hr class="mt-4 mb-4">
				</CCol>
			</CRow>
		</section>

		<section class="mt-3">
			<SelectSkuCategory
				:title="$t('message.select_sku_category')"
				:type.sync="selectCategorySkuType"
				:values="selectCategorySkuValues"
				:exclude-values="selectExcludeSkuValues"
				:check-all="selectCategorySkuCheckAll"
				:is-valid="formState.categorySku.valid"
				:disabled="isUser"
				@onDone="onSelectCategorySkuDone"
				@onDoneExcludeSku="onSelectExcludeSku"
			/>
			<CRow class="mt-4">
				<CCol>
					<hr class="mt-4 mb-2">
				</CCol>
			</CRow>
		</section>

		<BaseActionSubmit
			v-if="isAdmin"
			:is-loading="isLoading"
			:submit-text="$t('button.save_change')"
			:cancel-text="$t('button.cancel')"
			:remove-text="$t('button.remove')"
			@onSubmit="handleSubmit"
			@onCancel="handleCancel"
			@onRemove="handleOpenModalRemove"
		/>

		<!-- Modal Confirm Delete -->
		<ModalConfirmDelete
			ref="modal-confirm-delete"
			:show.sync="isShowModalConfirmDelete"
			:title="$t('message.do_you_want_to_remove_this')"
			:disabled-submit="isDeleting"
			@onSubmit="handleConfirmRemove"
			@onCancel="handleCancelRemove"
		/>
		<!-- End Modal Confirm Delete -->
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { required, maxLength, minValue, maxValue } from 'vuelidate/lib/validators';
import SelectSkuCategory from '../components/SelectSkuCategory.vue';
import SelectBranch from '../components/SelectBranch.vue';
import { inputStateValid, inputStateError } from '../helpers/vuelidator';
import { cloneDeep } from '../helpers/utils';
import { ROUTER_NAME } from '../enums/router';
import { ALLOCATION_BY } from '../enums/allocation';

export default {
	name: 'ChannelCreate',

	components: {
		SelectSkuCategory,
		SelectBranch,
	},

	data() {
		return {
			form: {
				name: null,
				priority: null,
				status: false,
				ratio: null,
				minQty: null,
				branchIds: [],
				isBranchAll: false,
				branchItecIds: [],
				categoryIds: [],
				isCategoryAll: false,
				skuIds: [],
				excludeSkuIds: [],
				isSkuAll: false,
				skuCodes: [],
				excludeSkuCodes: [],
			},
			selectedBranchValues: [],
			selectedBranchCheckAll: false,
			tempBranches: [],
			selectCategorySkuType: ALLOCATION_BY.CATEGOTY, // category or sku
			selectCategorySkuValues: [], // categories or sku ids
			selectCategorySkuCheckAll: false,
			selectExcludeSkuValues: [],
			isShowModalConfirmDelete: false,
			isDirty: false,
		};
	},

	validations: {
		form: {
			name: {
				required,
				maxLength: maxLength(255),
			},
			priority: {
				required,
				minValue: minValue(1),
				maxValue: maxValue(99),
			},
			ratio: {
				required,
			},
			minQty: {
				required,
			},
		},
	},

	computed: {
		...mapState('stockRuleSingle', {
			data: 'data',
			isLoadingGetSingle: 'isLoading',
		}),
		...mapState('stockRuleUpdate', {
			isLoading: 'isLoading',
			error: 'error',
		}),
		...mapState('stockRuleDelete', {
			isDeleting: 'isLoading',
			errorDelete: 'error',
		}),
		...mapGetters('stockRuleSingle', {
			getForm: 'getForm',
		}),
		...mapGetters('me', {
			isUser: 'isUser',
			isAdmin: 'isAdmin',
		}),
		formState() {
			return {
				name: {
					valid: inputStateValid(this.$v.form.name),
					error: inputStateError(this.$v.form.name)(this),
				},
				priority: {
					valid: inputStateValid(this.$v.form.priority),
					error: inputStateError(this.$v.form.priority)(this),
				},
				ratio: {
					valid: inputStateValid(this.$v.form.ratio),
					error: inputStateError(this.$v.form.ratio)(this),
				},
				minQty: {
					valid: inputStateValid(this.$v.form.minQty),
					error: inputStateError(this.$v.form.minQty)(this),
				},
				branch: {
					valid: (() => {
						if (!this.isDirty) {
							return null;
						}

						if (this.form.branchIds.length || this.form.isBranchAll) {
							return true;
						}

						return false;
					})(),
				},
				categorySku: {
					valid: (() => {
						if (!this.isDirty) {
							return null;
						}

						if (this.form.categoryIds.length || this.form.isCategoryAll) {
							return true;
						}

						if (this.form.skuIds.length || this.form.isSkuAll) {
							return true;
						}

						return false;
					})(),
				},
			};
		},
		formHelper() {
			// use IIFE becausse it's easy to use and more clearly in template
			// these properties can be null or string not an arrow function
			return {
				name: (() => {
					const isErrorMessageDisplay = this.formState.name.error !== null;
					return !isErrorMessageDisplay
						? this.$t('message.maximum_characters', { number: 255 })
						: null;
				})(),
				link: (() => {
					const isErrorMessageDisplay = this.formState.link.error !== null;
					return !isErrorMessageDisplay
						? this.$t('message.optional')
						: null;
				})(),
			};
		},
	},

	async created() {
		await this.fetchData();

		// set state form
		this.setState();

		// set page title
		this.setPageTitle({
			title: this.data.name,
			isShow: true,
		});
	},

	methods: {
		...mapActions('toast', {
			showToast: 'showToast',
		}),
		...mapActions('stockRuleSingle', {
			getStockRule: 'getStockRule',
		}),
		...mapActions('stockRuleUpdate', {
			updateStockRule: 'updateStockRule',
		}),
		...mapActions('stockRuleDelete', {
			removeStockRule: 'removeStockRule',
		}),
		...mapActions('pageTitle', {
			setPageTitle: 'setPageTitle',
		}),
		fetchData() {
			return this.getStockRule(this.$route.params.id);
		},
		setState() {
			// set form
			this.form = cloneDeep(this.getForm);
			// set branch ids
			this.selectedBranchValues = this.form.branches;

			const isTypeSku = this.form.isSkuAll || this.form.skuIds.length;

			// set sku/category type
			if (isTypeSku) {
				this.selectCategorySkuType = ALLOCATION_BY.SKU;
			} else {
				this.selectCategorySkuType = ALLOCATION_BY.CATEGOTY;
			}

			// set sku/category values
			if (isTypeSku) {
				this.selectCategorySkuValues = this.form.skuCodes;
			} else {
				this.selectCategorySkuValues = this.form.categoryIds;
			}

			this.selectExcludeSkuValues = this.form.excludeSkuCodes;

			this.tempBranches = this.data.branches.map((branch) => {
				return {
					branch_id: branch.itecBranchId,
					branch_id_of_im: branch.id,
				};
			});

			// set is check all
			this.selectedBranchCheckAll = this.form.isBranchAll;
			this.selectCategorySkuCheckAll = this.form.isSkuAll || this.form.isCategoryAll;
		},
		async handleSubmit() {
			this.$v.$touch();

			this.isDirty = true;

			const isValidForm = !this.$v.$invalid && this.formState.branch.valid && this.formState.categorySku.valid;

			if (isValidForm) {
				const getCategorySkuKeys = (form = {}) => {
					const keys = {};
					const isTabCategory = this.selectCategorySkuType === ALLOCATION_BY.CATEGOTY;
					const isTabSku = this.selectCategorySkuType === ALLOCATION_BY.SKU;
					if (isTabCategory) {
						keys.is_all_categories = form.isCategoryAll;
						keys.category_ids = form.isCategoryAll ? [] : form.categoryIds;
					} else if (isTabSku) {
						keys.is_all_products = false;
						keys.itec_skus = form.isSkuAll ? [] : form.skuIds;
						keys.product_ids = [];
					}
					return keys;
				};

				const getBranchKeys = (form = {}) => {
					return {
						is_all_branches: form.isBranchAll,
						branch_ids: form.isBranchAll ? [] : form.branchIds,
					};
				};

				const data = {
					name: this.form.name,
					online_ratio: Number(this.form.ratio || 0),
					minimum: Number(this.form.minQty || 0),
					priority: Number(this.form.priority || 1),
					...getBranchKeys(this.form),
					...getCategorySkuKeys(this.form),
					itec_sku_exclusions: this.form.excludeSkuIds,
					is_active: Boolean(this.form.status),
				};

				await this.updateStockRule({
					id: this.$route.params.id,
					params: data,
				});

				const { error } = this;

				if (error === null) {
				// show toast with success
					this.showToast({
						header: this.$t('toast.success'),
						content: this.$t('toast.update_rule'),
						type: 'success',
					});
					// refresh data info, request to api to get data again
					await this.fetchData();
					// set state form
					this.setState();
				} else {
				// show toast with error
					this.showToast({
						header: this.$t('toast.unable_to_save'),
						content: error.message,
						type: 'danger',
					});
				}
			}
		},
		handleCancel() {
			// redirect to list pages
			this.$router.push({ name: ROUTER_NAME.ALOCATION_ONLINE_STOCK_LIST });
		},
		handleOpenModalRemove() {
			this.$refs['modal-confirm-delete'].open();
		},
		async handleConfirmRemove() {
			await this.removeStockRule(this.$route.params.id);

			if (this.errorDelete === null) {
				this.isShowModalConfirmDelete = false;

				this.showToast({
					header: this.$t('toast.success'),
					content: this.$t('toast.data_has_been_removed'),
					type: 'success',
				});

				// redirect to list pages
				this.$router.push({ name: ROUTER_NAME.ALOCATION_ONLINE_STOCK_LIST });
			} else {
				this.showToast({
					header: this.$t('toast.unable_to_remove'),
					content: this.errorDelete.message,
					type: 'danger',
				});
			}
		},
		handleCancelRemove() {
			this.isShowModalConfirmDelete = false;
		},
		onSelectBranchDone(values = []) {
			this.form.branchIds = values.map((branch) => branch.id);
			this.selectedBranchValues = values;
		},
		onSelectCategorySkuDone(type = '', values = [], isSelectedAll = false) {
			this.selectCategorySkuType = type;

			if (type === ALLOCATION_BY.CATEGOTY) {
				this.form.categoryIds = values.map(({ id }) => id);
				this.form.isCategoryAll = isSelectedAll;
			} else if (type === ALLOCATION_BY.SKU) {
				this.form.skuIds = values;
				this.form.isSkuAll = isSelectedAll;
			}
		},
		onSelectExcludeSku(values = []) {
			this.form.excludeSkuIds = values;
		},
	},
};
</script>

<style lang="scss" scoped>
.form-custom {
	width: rem(600);
}
</style>
